import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="state-persistence"
export default class extends Controller {
  static values = {
    path: String,         // Path to match for saving state (e.g., '/property_leads')
    storageKey: String,   // LocalStorage key to use (e.g., 'propertyLeadsListState')
    isListView: Boolean   // Whether this is the list view (true) or detail view (false)
  }
  
  static targets = ["form", "back"]
  
  connect() {
    // If this is the list view, save the current URL state
    if (this.isListViewValue) {
      this.saveListState()
    }
    
    // When the page loads, check if we need to restore state
    this.restoreStateIfNeeded()
  }
  
  // Save the current list view state to localStorage
  saveListState() {
    if (this.pathValue && window.location.pathname === this.pathValue) {
      // Check if we have meaningful query parameters
      if (this.hasMeaningfulQueryParams()) {
        // Get the current URL and decode any %2C to commas
        let currentUrl = window.location.href
        currentUrl = currentUrl.replace(/%2C/g, ',')
        
        localStorage.setItem(this.storageKeyValue, currentUrl)
        console.log(`List state saved: ${currentUrl}`)
      } else {
        // If there are no meaningful query parameters, clear the saved state
        localStorage.removeItem(this.storageKeyValue)
        console.log('List state cleared (no meaningful filters)')
      }
    }
  }
  
  // Check if the current URL has meaningful query parameters
  hasMeaningfulQueryParams() {
    const searchParams = new URLSearchParams(window.location.search)
    
    // If there are no search params, return false
    if (searchParams.toString() === '') return false
    
    // Check for pagination and sorting parameters first
    // These should be preserved even without filters
    if (searchParams.has('page') || searchParams.has('sort') || searchParams.has('direction')) {
      return true
    }
    
    // Check each parameter to see if it has a meaningful value
    for (const [name, value] of searchParams.entries()) {
      // If the parameter has a non-empty value, it's meaningful
      if (value.trim() !== '') return true
    }
    
    // If we get here, there are no meaningful parameters
    return false
  }
  
  // Handle form submission (Stimulus action)
  handleFormSubmit(event) {
    // We'll handle this after the form submits and the page updates
    // by checking the URL for meaningful query parameters
    setTimeout(() => this.saveListState(), 100)
  }
  
  // Handle back button click (Stimulus action)
  handleBackClick(event) {
    const link = event.currentTarget;
    const linkPath = link.pathname;

    // If the link already points to a specific path (like the pipeline), let it proceed.
    if (linkPath !== this.pathValue) {
      return; // Allow the default link behavior
    }

    // If the link points to the base path, check for a saved state.
    const savedState = localStorage.getItem(this.storageKeyValue);
    if (savedState) {
      event.preventDefault();
      const restoredUrl = savedState.replace(/%2C/g, ',');
      console.log(`Navigating back to saved state: ${restoredUrl}`);
      window.location.href = restoredUrl;
    }
    // If no saved state, the default link behavior (to the base path) is fine.
  }
  
  // Check if we need to restore state when loading the page
  restoreStateIfNeeded() {
    // Only restore state on the list view with no query parameters
    if (this.isListViewValue && 
        this.pathValue && 
        window.location.pathname === this.pathValue && 
        !window.location.search) {
      
      let savedState = localStorage.getItem(this.storageKeyValue)
      
      if (savedState) {
        // Replace %2C with commas for better readability
        savedState = savedState.replace(/%2C/g, ',')
        console.log(`Restoring list state: ${savedState}`)
        window.location.href = savedState
      }
    }
  }
}
